<template>
  <premium-section feature="integrations-in-element">
    <div v-if="! outlookIsConnected && ! gcalIsConnected">
      <div class="d-flex align-center"><v-icon start>far fa-share-alt</v-icon>{{ $t('ACCOUNT.INTEGRATIONS') }}</div>
      <div v-if="outlookIsLoading && gcalIsLoading" class="ml-6"><i class="far fa-spinner fa-spin fa-2x fa-fw"></i></div>
      <div v-else class="mt-1 d-flex align-center">
        <img :alt="$t('INTEGRATIONS.MICROSOFT_OUTLOOK')" :title="$t('INTEGRATIONS.MICROSOFT_OUTLOOK')" src="../../img/integrations/outlook.png" style="max-height: 28px">
        <img :alt="$t('INTEGRATIONS.GOOGLE_CALENDAR')" :title="$t('INTEGRATIONS.GOOGLE_CALENDAR')" src="../../img/integrations/gcal.png" class="ml-2" style="max-height: 28px">
        <v-btn size="small" rounded variant="outlined" class="mx-2" @click="openIntegrationsWindow()">
          {{ $t('INTEGRATIONS.CONNECT') }}
        </v-btn>
      </div>
    </div>
    <div v-if="outlookIsConnected" :style="{ opacity: outlookIsLoading ? .5 : '' }" class="mb-1">
      <div class="d-flex align-center"><v-icon start>far fa-share-alt</v-icon>{{ $t('INTEGRATIONS.MICROSOFT_OUTLOOK') }}</div>
      <div class="mt-1 d-flex align-center">
        <img src="../../img/integrations/outlook.png" style="max-height: 28px;">
        <div :style="{ 'pointer-events': outlookIsLoading ? 'none' : '' }" class="ml-2">
          <div v-if="outlookIsLoading">
            <i class="far fa-spinner fa-spin fa-2x fa-fw"></i>
          </div>
          <div v-else class="d-flex align-center">
            <v-switch v-if="outlookIsConnected" :model-value="outlookStatus" @click="toggleOutlook">
              <template #label>
                <span class="small">{{ outlookStatus ? $t('INTEGRATIONS.SYNCHRONIZED') : '' }}</span>
              </template>
            </v-switch>
            <div v-if="microsoftError" class="ml-2">
              <i class="far fa-lg fa-exclamation-triangle text-errorred"></i> {{ microsoftError }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="gcalIsConnected" :style="{ opacity: gcalIsLoading ? .5 : '' }" class="mb-1">
      <div class="d-flex align-center"><v-icon start>far fa-share-alt</v-icon>{{ $t('INTEGRATIONS.GOOGLE_CALENDAR') }}</div>
      <div class="mt-1 d-flex align-center">
        <img src="../../img/integrations/gcal.png" style="max-height: 28px;">
        <div :style="{ 'pointer-events': gcalIsLoading ? 'none' : '' }" class="ml-2">
          <div v-if="gcalIsLoading">
            <i class="far fa-spinner fa-spin fa-2x fa-fw"></i>
          </div>
          <div v-else class="d-flex align-center">
            <v-switch v-if="gcalIsConnected" :model-value="gcalStatus" @click="toggleGcal">
              <template #label>
                <span class="small">{{ gcalStatus ? $t('INTEGRATIONS.SYNCHRONIZED') : '' }}</span>
              </template>
            </v-switch>
            <div v-if="googleError" class="ml-2">
              <i class="far fa-lg fa-exclamation-triangle text-errorred"></i> {{ googleError }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </premium-section>
</template>

<script>
  import useMicrosoftIntegrationStore from '@/stores/integrations/microsoft';
  import useGoogleIntegrationStore from '@/stores/integrations/google';
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  export default {
    props: {
      el: { type: Object, required: true },
    },
    emits: ['close'],
    data() {
      return {
        microsoftError: "",
        googleError: "",
        accessRightStore: useUsersAccessRightStore(),
        integrationStores: {
          microsoft: useMicrosoftIntegrationStore(),
          google: useGoogleIntegrationStore(),
        },
      };
    },
    computed: {
      outlookIsConnected() {
        return this.integrationStores.microsoft.outlook.connected;
      },
      outlookIsLoading() {
        return this.integrationStores.microsoft.outlook.loading;
      },
      outlookStatus() {
        return this.integrationStores.microsoft.getElementStatus(this.el);
      },
      gcalIsConnected() {
        return this.integrationStores.google.gcal.connected;
      },
      gcalIsLoading() {
        return this.integrationStores.google.gcal.loading;
      },
      gcalStatus() {
        return this.integrationStores.google.getElementStatus(this.el);
      },
    },
    created() {
      if (this.outlookIsConnected === null) {
        this.integrationStores.microsoft.refreshConnection().catch((message) => {
          this.microsoftError = message || this.$t('GLOBAL.ERROR');
        });
      }
      if (this.gcalIsConnected === null) {
        this.integrationStores.google.refreshConnection().catch((message) => {
          this.googleError = message || this.$t('GLOBAL.ERROR');
        });
      }
    },
    methods: {
      toggleOutlook() {
        this.microsoftError = '';
        this.integrationStores.microsoft.setElementStatus({ el: this.el, sync: this.outlookStatus ? -1 : 1 }).then((response) => {
          if (this.outlookStatus && this.el.hasDates()) {
            return this.integrationStores.microsoft.postEvent({ el: this.el });
          }
          return response;
        }).catch((message) => {
          this.microsoftError = message || this.$t('GLOBAL.ERROR');
        });
      },
      toggleGcal() {
        this.googleError = '';
        this.integrationStores.google.setElementStatus({ el: this.el, sync: this.gcalStatus ? -1 : 1 }).then((response) => {
          if (this.gcalStatus && this.el.hasDates()) {
            return this.integrationStores.google.postEvent({ el: this.el });
          }
          return response;
        }).catch((message) => {
          this.googleError = message || this.$t('GLOBAL.ERROR');
        });
      },
      openIntegrationsWindow() {
        this.$emit('close');
        setTimeout(() => {
          this.$router.push('/manage#profile-integrations');
        });
      },
    },
  };
</script>
